//>>> Environment configuration with domain-aware API endpoints
// Vite exposes only VITE_* vars. Restart dev server after changing .env.
// API_URL follows @utils/domain (owner vs tenant host).
import { detectDomain } from '@utils/domain';

const mode = import.meta.env.MODE;
const isProd = import.meta.env.PROD;

const trimOrEmpty = (v) => (typeof v === 'string' ? v.trim() : '');

const tenantLocal = trimOrEmpty(import.meta.env.VITE_TENANT_API_URL_LOCAL);
const tenantStagging = trimOrEmpty(import.meta.env.VITE_TENANT_API_URL_STAGGING);
const tenantProduction = trimOrEmpty(import.meta.env.VITE_TENANT_API_URL_PRODUCTION);
const tenantByMode =
    trimOrEmpty(import.meta.env.VITE_TENANT_API_URL) ||
    (mode === 'production' || mode === 'production.v2'
        ? tenantProduction
        : mode === 'stagging' || mode === 'stagging.v2'
            ? tenantStagging
            : tenantLocal);

const ownerLocal = trimOrEmpty(import.meta.env.VITE_OWNER_API_URL_LOCAL);
const ownerLive = trimOrEmpty(import.meta.env.VITE_OWNER_API_URL_LIVE) || trimOrEmpty(import.meta.env.VITE_OWNER_API_URL);

const resolveTenantBase = () => {
    return tenantByMode;
};

const resolveOwnerBase = () => {
    if (mode === 'production' || mode === 'production.v2' || mode === 'stagging' || mode === 'stagging.v2') return ownerLive;
    return ownerLocal;
};

const OWNER_API_BASE = resolveOwnerBase();
const TENANT_API_BASE = resolveTenantBase();

const currentDomain = detectDomain();

const getApiUrl = () => {
    if (currentDomain === 'owner') {
        return `${OWNER_API_BASE}`;
    }
    return `${TENANT_API_BASE}`;
};

const getOwnerApiUrl = () => `${OWNER_API_BASE}`;

const getTenantApiUrl = () => `${TENANT_API_BASE}`;

const ENV = {
    APP_NAME: import.meta.env.VITE_APP_NAME || 'Project Name',
    PRODUCT_LOGO_PATH: import.meta.env.VITE_PRODUCT_LOGO_PATH || '/logo.png',
    PRODUCT_FAVICON_PATH: import.meta.env.VITE_PRODUCT_FAVICON_PATH || '/favicon.png',

    MODE: import.meta.env.MODE,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,

    // Domain detection
    DOMAIN: currentDomain,
    IS_OWNER: currentDomain === 'owner',
    IS_TENANT: currentDomain === 'tenant',

    // Current API URL (domain-aware)
    API_URL: getApiUrl(),

    // Specific API URLs
    OWNER_API_URL: getOwnerApiUrl(),
    TENANT_API_URL: getTenantApiUrl(),

    TENANT_API_URL_LOCAL: tenantLocal,
    TENANT_API_URL_STAGGING: tenantStagging,
    TENANT_API_URL_PRODUCTION: tenantProduction,
    OWNER_API_URL_LOCAL: ownerLocal,
    OWNER_API_URL_LIVE: ownerLive,

    // Legacy support (will use domain-aware API_URL)
    API_URL_STREAM: import.meta.env.VITE_API_URL_STREAM || '',
    FRONT_FACING_URL: import.meta.env.VITE_FRONT_FACING_URL || '',
    BASE_URL: import.meta.env.VITE_BASE_URL || '',

    // Environment (auth store keys off development vs production)
    NODE_ENV: import.meta.env.DEV ? 'development' : 'production',
    PORT: import.meta.env.PORT || 3000,
    HOST: import.meta.env.HOST || '0.0.0.0',
    SSL: import.meta.env.SSL || false,
    SSL_KEY: import.meta.env.SSL_KEY || '',
    SSE_RETRY_MS: Number(import.meta.env.VITE_SSE_RETRY_MS) || 3000,

    /**
     * When many HTTP requests stay in-flight for a long time (e.g. tab backgrounded),
     * try a token refresh + socket reconnect instead of forcing logout.
     * `VITE_HTTP_MAX_INFLIGHT_BEFORE_RECOVERY` defaults to 10 if unset or invalid.
     */
    HTTP_MAX_INFLIGHT_BEFORE_RECOVERY: Math.max(
        1,
        Number.parseInt(String(import.meta.env.VITE_HTTP_MAX_INFLIGHT_BEFORE_RECOVERY ?? ''), 10) || 10,
    ),
    /** Age (ms) of the oldest in-flight request before triggering soft recovery. Default 2 minutes. */
    HTTP_INFLIGHT_AGE_MS_BEFORE_RECOVERY:
        Number.parseInt(String(import.meta.env.VITE_HTTP_INFLIGHT_AGE_MS_BEFORE_RECOVERY ?? ''), 10) || 120000,
};

export { ENV };
